import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthGuard } from '../components/common/AuthGuard'
import {
  AdminOnly,
  CreateSolicitudGate,
} from '../components/common/PermissionGate'
import { Layout } from '../components/layout'
import { JobPostingsTable } from '../components/dashboard/JobPostingsTable'
import WorkflowViewer from '../components/dashboard/WorkflowViewer'

import { usePermissions } from '../hooks/usePermissions'

import { apiService } from '../services/api'
import { getErrorMessage } from '../utils/errorHandling'
import type { SolicitudListItem, SolicitudesResponse } from '../types'

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const permissions = usePermissions()

  // State for job postings data
  const [jobPostings, setJobPostings] = useState<SolicitudListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [_totalItems, setTotalItems] = useState(0)
  const [nextKey, setNextKey] = useState<string | undefined>()

  // State for pagination and filtering
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<string>('fecha_creacion')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // State for workflow viewer
  const [selectedSolicitudForWorkflow, setSelectedSolicitudForWorkflow] =
    useState<string | null>(null)

  // Load job postings data
  const loadJobPostings = useCallback(
    async (startKey?: string, reset = false) => {
      try {
        setIsLoading(true)
        setError(null)

        const response: SolicitudesResponse = await apiService.getSolicitudes(
          pageSize,
          startKey
        )

        const newItems = response.data.items || []

        if (reset) {
          setJobPostings(newItems)
        } else {
          setJobPostings(prev => [...prev, ...newItems])
        }

        setTotalItems(response.data.total_items || 0)
        setNextKey(response.data.next_key)
      } catch (err) {
        console.error('Error loading job postings:', err)
        setError(getErrorMessage(err) || 'Error al cargar las solicitudes')
      } finally {
        setIsLoading(false)
      }
    },
    [pageSize]
  )

  // Initial load
  useEffect(() => {
    loadJobPostings(undefined, true)
  }, [loadJobPostings])

  // Handle pagination
  const handleLoadMore = () => {
    if (nextKey && !isLoading) {
      loadJobPostings(nextKey, false)
    }
  }

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    // Reset and reload with new page size
    setJobPostings([])
    setNextKey(undefined)
  }

  // Handle search
  const handleSearch = (term: string) => {
    setSearchTerm(term)
    // In a real implementation, you would filter the results
    // For now, we'll just store the search term
  }

  // Handle sorting
  const handleSort = (field: string, order: 'asc' | 'desc') => {
    setSortField(field)
    setSortOrder(order)

    // Sort current data locally
    const sortedData = [...jobPostings].sort((a, b) => {
      let aValue: any = a[field as keyof SolicitudListItem]
      let bValue: any = b[field as keyof SolicitudListItem]

      // Handle nested fields
      if (field === 'puesto') {
        aValue = a.jd?.estructura?.puesto_solicitado?.puesto || ''
        bValue = b.jd?.estructura?.puesto_solicitado?.puesto || ''
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })

    setJobPostings(sortedData)
  }

  // Handle navigation to candidates page
  const handleViewCandidates = (solicitudId: string) => {
    navigate(`/candidates/${solicitudId}`)
  }

  // Handle navigation to requirement view
  const handleViewRequirement = (solicitudId: string) => {
    navigate(`/candidates/${solicitudId}?tab=requirement`)
  }

  // Handle navigation to requirement creation
  const handleCreateRequirement = () => {
    navigate('/new-requirement')
  }

  // Handle workflow viewer
  const handleViewWorkflow = (solicitudId: string) => {
    setSelectedSolicitudForWorkflow(solicitudId)
  }

  const handleCloseWorkflowViewer = () => {
    setSelectedSolicitudForWorkflow(null)
  }

  // Handle refresh
  const handleRefresh = () => {
    setJobPostings([])
    setNextKey(undefined)
    loadJobPostings(undefined, true)
  }

  // Filter job postings based on search term
  const filteredJobPostings = jobPostings.filter(posting => {
    if (!searchTerm) return true

    const searchLower = searchTerm.toLowerCase()
    const puesto =
      posting.jd?.estructura?.puesto_solicitado?.puesto?.toLowerCase() || ''
    const id = posting.id_solicitud.toLowerCase()

    return puesto.includes(searchLower) || id.includes(searchLower)
  })

  return (
    <AuthGuard>
      <Layout showBreadcrumbs={false}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Action Bar */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">
                Solicitudes de Vacante
              </h2>
              <p className="text-secondary-600">
                Gestiona y supervisa todas tus solicitudes activas
              </p>
            </div>
            <CreateSolicitudGate>
              <button
                type="button"
                className="btn-primary flex items-center space-x-2 animate-bounce-subtle"
                onClick={() => navigate('/new-requirement')}
              >
                <i className="fa-solid fa-plus"></i>
                <span>Crear nuevo requerimiento</span>
              </button>
            </CreateSolicitudGate>
          </div>

          {/* Solicitudes Table */}
          <div className="card animate-fade-in">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Solicitudes de Trabajo
                </h3>
                <button
                  className="btn-secondary text-sm"
                  onClick={handleRefresh}
                  disabled={isLoading}
                >
                  Actualizar
                </button>
              </div>

              {/* Filters and Search */}
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  placeholder="Buscar por posición o ID..."
                  value={searchTerm}
                  onChange={e => handleSearch(e.target.value)}
                  className="input-modern flex-1"
                />

                <select
                  value={pageSize}
                  onChange={e => handlePageSizeChange(Number(e.target.value))}
                  className="input-modern w-full sm:w-48"
                >
                  <option value={5}>5 elementos</option>
                  <option value={10}>10 elementos</option>
                  <option value={20}>20 elementos</option>
                  <option value={50}>50 elementos</option>
                </select>
              </div>
            </div>

            <JobPostingsTable
              data={filteredJobPostings}
              isLoading={isLoading}
              error={error}
              onSort={handleSort}
              onViewCandidates={handleViewCandidates}
              onViewRequirement={handleViewRequirement}
              onViewWorkflow={handleViewWorkflow}
              onCreateRequirement={handleCreateRequirement}
              onLoadMore={nextKey ? handleLoadMore : undefined}
              hasMore={!!nextKey}
              sortField={sortField}
              sortOrder={sortOrder}
            />
          </div>
        </div>

        {/* Workflow Viewer Modal */}
        {selectedSolicitudForWorkflow && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <WorkflowViewer
                solicitudId={selectedSolicitudForWorkflow}
                onClose={handleCloseWorkflowViewer}
                refreshInterval={30000}
              />
            </div>
          </div>
        )}
      </Layout>
    </AuthGuard>
  )
}

export default Dashboard
